import { ChangeDetectionStrategy, Component, input, Inject, Optional, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyColumnDef, GalaxyDataSource } from '@vendasta/galaxy/table';
import { GalaxyTableModule } from '@vendasta/galaxy/table';
import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyFilterChipsModule } from '@vendasta/galaxy/filter/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { ActivityLeaderboardDatasource } from './activity-leaderboard.datasource';
import { Sort } from '@angular/material/sort';
import { DateRange, UserActivity, ActivityLeaderboardFilter, ActivityCount } from './interfaces';
import { GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import {
  ActivityFilterDefinitionsService,
  ACTIVITY_FILTER_DEFINITIONS_SERVICE_TOKEN,
} from './activity-filter-definitions.service';
import { map, Observable, startWith, switchMap, take } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

type FooterType = 'total' | 'average';

@Component({
  selector: 'crm-activity-leaderboard',
  templateUrl: './activity-leaderboard.component.html',
  styleUrls: ['./activity-leaderboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    GalaxyTableModule,
    MatTableModule,
    MatSelectModule,
    MatSortModule,
    GalaxyFormFieldModule,
    GalaxyAvatarModule,
    GalaxyFilterChipsModule,
    MatButtonModule,
    MatIconModule,
    MatBadgeModule,
  ],
  providers: [ActivityLeaderboardDatasource],
})
export class ActivityLeaderboardComponent {
  selection = input<DateRange>();

  protected readonly columns: GalaxyColumnDef[] = [
    {
      id: 'rank',
      title: 'LEADERBOARD.COLUMNS.RANK',
    },
    {
      id: 'name',
      title: 'LEADERBOARD.COLUMNS.NAME',
      pinned: true,
      sticky: true,
    },
    {
      id: 'inboundCall',
      title: 'LEADERBOARD.COLUMNS.INBOUND_CALLS',
    },
    {
      id: 'outboundCall',
      title: 'LEADERBOARD.COLUMNS.OUTBOUND_CALLS',
    },
    {
      id: 'inboundEmail',
      title: 'LEADERBOARD.COLUMNS.INBOUND_EMAILS',
    },
    {
      id: 'outboundEmail',
      title: 'LEADERBOARD.COLUMNS.OUTBOUND_EMAILS',
    },
    {
      id: 'meeting',
      title: 'LEADERBOARD.COLUMNS.MEETINGS',
    },
  ];
  protected readonly tableColumns = this.columns.map((c) => c.id);

  protected readonly dataSource = new GalaxyDataSource<UserActivity, ActivityLeaderboardFilter, Sort>(
    this.activityDatasource,
  );

  protected get tableDataSource(): GalaxyDataSource<UserActivity, unknown, unknown> {
    return this.dataSource as GalaxyDataSource<UserActivity, unknown, unknown>;
  }

  protected readonly footerType = signal<FooterType>('total');
  protected readonly filtersAvailable: boolean;
  protected readonly activeFiltersCount$: Observable<boolean>;
  protected readonly footerData$: Observable<ActivityCount>;

  protected setFooterType(type: FooterType): void {
    this.footerType.set(type);
  }

  constructor(
    private readonly activityDatasource: ActivityLeaderboardDatasource,
    @Optional()
    @Inject(ACTIVITY_FILTER_DEFINITIONS_SERVICE_TOKEN)
    private readonly activityFilterService: ActivityFilterDefinitionsService | undefined,
  ) {
    // Check if filters are available
    this.filtersAvailable = !!this.activityFilterService && this.activityFilterService.hasFilters;

    // Create observable for active filters count
    this.activeFiltersCount$ = this.dataSource.filtersApplied$.pipe(
      map((filters) => (this.filtersAvailable ? filters : false)),
    );

    // Create observable for footer data that switches between total and average
    const footerType$ = toObservable(this.footerType);

    this.footerData$ = footerType$.pipe(
      switchMap((type) => {
        return type === 'average' ? this.activityDatasource.average$$ : this.activityDatasource.total$$;
      }),
      startWith({
        inboundCall: 0,
        outboundCall: 0,
        inboundEmail: 0,
        outboundEmail: 0,
        meeting: 0,
      } as ActivityCount),
    );

    // React to changes in selection input (date range changes)
    effect(
      () => {
        this.updateFilters([]);
      },
      { allowSignalWrites: true },
    );
  }

  protected sortChange(sort: Sort): void {
    this.dataSource.setSorting([sort]);
  }

  protected onFiltersChanged(filters: GalaxyFilterInterface[]): void {
    this.updateFilters(filters);
  }

  private updateFilters(filters: GalaxyFilterInterface[]): void {
    const dateRange = this.selection();

    if (this.activityFilterService) {
      this.activityFilterService
        .processFilters(filters)
        .pipe(take(1))
        .subscribe((filterCriteria) => {
          const combinedFilter: ActivityLeaderboardFilter = {
            dateRange: dateRange,
            salesTeamIds: filterCriteria?.salesTeamIds || [],
          };
          this.dataSource.setFilters([combinedFilter]);
        });
    } else {
      const combinedFilter: ActivityLeaderboardFilter = {
        dateRange: dateRange,
        salesTeamIds: [],
      };
      this.dataSource.setFilters([combinedFilter]);
    }
  }
}
